import React, { useRef } from "react";
import clsx from "clsx";
import { useVirtualizer } from "@tanstack/react-virtual";
import styles from "./LazyList.module.scss";
import { useVisibility } from "../Visibility/Visibility";
import { withRefProp } from "../../../utils/withRefProp";

export interface LazyListProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
  /** Ref forwarded to the inner scroll container, not the root wrapper. */
  scrollRef?: React.Ref<HTMLDivElement>;
  handleRef?: React.Ref<LazyListHandle>;
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  renderSkeletonItem?: (index: number) => React.ReactNode;
  getItemKey: (item: T, index: number) => string | number;
  renderEmpty?: () => React.ReactNode;
  virtualizeThreshold?: number;
  onReachEnd?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  reachEndOffset?: number;
  skeletonRows?: number;
  className?: string;
  scrollClassName?: string;
}

export interface LazyListHandle {
  scrollToIndex: (index: number) => void;
}

const DEFAULT_VIRTUALIZE_THRESHOLD = 25;
const FALLBACK_ROW_HEIGHT = 58;
const ESTIMATE_WINDOW_SIZE = 50;

function RowsSimple<T>({
  items,
  renderRow,
  getItemKey,
  scrollClassName,
  scrollRef: outerScrollRef,
  onReady,
}: {
  items: T[];
  renderRow: (item: T, index: number) => React.ReactNode;
  getItemKey: (item: T, index: number) => string | number;
  scrollClassName?: string;
  scrollRef?: React.Ref<HTMLDivElement>;
  onReady?: (api: LazyListHandle) => void;
}) {
  const innerRef = React.useRef<HTMLDivElement | null>(null);

  const setRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof outerScrollRef === "function") outerScrollRef(node);
      else if (outerScrollRef) (outerScrollRef as { current: HTMLDivElement | null }).current = node;
    },
    [outerScrollRef],
  );

  React.useEffect(() => {
    onReady?.({
      scrollToIndex: (index: number) => {
        if (!innerRef.current) return;
        const node = innerRef.current.querySelector<HTMLElement>(`[data-lazy-list-index="${index}"]`);
        node?.scrollIntoView({ block: "nearest" });
      },
    });
  }, [onReady]);

  return (
    <div ref={setRef} className={clsx(styles.scroll, scrollClassName)} data-scroll-list="true">
      {items.map((item, index) => (
        <div key={getItemKey(item, index)} data-lazy-list-index={index}>
          {renderRow(item, index)}
        </div>
      ))}
    </div>
  );
}

function RowsVirtual<T>({
  items,
  renderRow,
  getItemKey,
  scrollClassName,
  scrollRef: outerScrollRef,
  hasMore = false,
  loadingMore = false,
  onReachEnd,
  onReady,
}: {
  items: T[];
  renderRow: (item: T, index: number) => React.ReactNode;
  getItemKey: (item: T, index: number) => string | number;
  scrollClassName?: string;
  scrollRef?: React.Ref<HTMLDivElement>;
  hasMore?: boolean;
  loadingMore?: boolean;
  onReachEnd?: () => void;
  onReady?: (api: LazyListHandle) => void;
}) {
  const parentRef = React.useRef<HTMLDivElement | null>(null);

  const setScrollRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      parentRef.current = node;
      if (typeof outerScrollRef === "function") outerScrollRef(node);
      else if (outerScrollRef) (outerScrollRef as { current: HTMLDivElement | null }).current = node;
    },
    [outerScrollRef],
  );
  const measuredRef = React.useRef(new Map<number, number>());
  const contentWindowRef = React.useRef<number[]>([]);
  const skeletonWindowRef = React.useRef<number[]>([]);
  const [contentEstimate, setContentEstimate] = React.useState(FALLBACK_ROW_HEIGHT);
  const [skeletonEstimate, setSkeletonEstimate] = React.useState(FALLBACK_ROW_HEIGHT);

  const recomputeEstimate = React.useCallback((isSkeleton: boolean) => {
    const values = isSkeleton ? skeletonWindowRef.current : contentWindowRef.current;
    if (values.length === 0) return;
    const avg = Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
    if (isSkeleton) {
      setSkeletonEstimate((current) => (Math.abs(current - avg) >= 1 ? avg : current));
    } else {
      setContentEstimate((current) => (Math.abs(current - avg) >= 1 ? avg : current));
    }
  }, []);

  const updateRollingWindow = React.useCallback(
    (index: number, height: number, isSkeleton: boolean) => {
      const prev = measuredRef.current.get(index);
      measuredRef.current.set(index, height);
      if (typeof prev === "number") {
        const prevWindow = (items[index] === null) ? skeletonWindowRef.current : contentWindowRef.current;
        const pos = prevWindow.indexOf(prev);
        if (pos >= 0) prevWindow.splice(pos, 1);
      }
      const nextWindow = isSkeleton ? skeletonWindowRef.current : contentWindowRef.current;
      nextWindow.push(height);
      if (nextWindow.length > ESTIMATE_WINDOW_SIZE) {
        nextWindow.shift();
      }
      recomputeEstimate(isSkeleton);
    },
    [items, recomputeEstimate],
  );

  React.useEffect(() => {
    measuredRef.current.clear();
    contentWindowRef.current = [];
    skeletonWindowRef.current = [];
    setContentEstimate(FALLBACK_ROW_HEIGHT);
    setSkeletonEstimate(FALLBACK_ROW_HEIGHT);
  }, [items.length]);

  const { visible } = useVisibility();

  const virtualizer = useVirtualizer({
    enabled: visible,
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (items[index] === null ? skeletonEstimate : contentEstimate),
    overscan: 8,
    getItemKey: (index) => getItemKey(items[index], index),
  });
  const virtualItems = virtualizer.getVirtualItems();

  const previousLastItemIndexRef = useRef(-1);

  React.useEffect(() => {
    onReady?.({
      scrollToIndex: (index: number) => {
        virtualizer.scrollToIndex(index, { align: "auto" });
      },
    });
  }, [onReady, virtualizer]);

  React.useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;
    if (!hasMore || !onReachEnd) return;
    if (lastItem.index >= items.length - 1 && previousLastItemIndexRef.current !== lastItem.index) {
      previousLastItemIndexRef.current = lastItem.index;
      onReachEnd();
    }
  }, [virtualItems, items.length, hasMore, onReachEnd]);

  return (
    <div ref={setScrollRef} role="list" data-scroll-list="true" className={clsx(styles.scroll, scrollClassName)}>
      <div style={{ height: virtualizer.getTotalSize(), width: "100%", position: "relative" }}>
        {virtualItems.map((virtualItem: { key: React.Key; index: number; start: number }) => (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={(node) => {
              if (!node) return;
              const measured = node.getBoundingClientRect().height;
              if (measured > 0) {
                updateRollingWindow(virtualItem.index, measured, items[virtualItem.index] === null);
              }
              virtualizer.measureElement(node);
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {renderRow(items[virtualItem.index], virtualItem.index)}
          </div>
        ))}
      </div>
    </div>
  );
}

const LazyList = withRefProp(<T,>({
  items = [] as T[],
  renderItem,
  renderSkeletonItem,
  getItemKey,
  renderEmpty,
  virtualizeThreshold = DEFAULT_VIRTUALIZE_THRESHOLD,
  onReachEnd,
  hasMore = false,
  loadingMore = false,
  reachEndOffset = 180,
  skeletonRows = 0,
  handleRef,
  ref,
  scrollRef,
  className,
  scrollClassName,
  ...props
}: LazyListProps<T>) => {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const shouldVirtualize =
    process.env.NODE_ENV === "test" ? false : (Boolean(onReachEnd) || items.length > virtualizeThreshold);
  const clampedSkeletonRows = Math.max(0, skeletonRows);
  const totalItems = items.length + clampedSkeletonRows;
  const rows = React.useMemo(
    () => Array.from({ length: totalItems }, (_, idx) => (idx < items.length ? items[idx] : null)),
    [items, totalItems],
  );
  const renderRow = React.useCallback(
    (item: T | null, index: number) => {
      if (item === null) {
        const skeletonIndex = index - items.length;
        if (skeletonIndex < clampedSkeletonRows && renderSkeletonItem) {
          return renderSkeletonItem(skeletonIndex);
        }
        return null;
      }
      return renderItem(item, index);
    },
    [clampedSkeletonRows, items.length, renderItem, renderSkeletonItem],
  );

  const setHandle = React.useCallback((api: LazyListHandle) => {
    if (!handleRef) return;
    if (typeof handleRef === "function") handleRef(api);
    else handleRef.current = api;
  }, [handleRef]);


  return (
    <div
      ref={(node) => {
        rootRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref && typeof ref === "object") ref.current = node;
      }}
      className={clsx(styles.root, className)}
      {...props}
    >
      {props.children}
      {totalItems === 0
        ? <div className={clsx(styles.scroll, scrollClassName)} data-scroll-list="true">{renderEmpty?.() ?? null}</div>
        : shouldVirtualize
          ? <RowsVirtual
              items={rows as Array<T | null>}
              renderRow={renderRow as (item: T | null, index: number) => React.ReactNode}
              getItemKey={(item, index) => (index < items.length ? getItemKey(item as T, index) : `__skeleton__${index}`)}
              scrollClassName={scrollClassName}
              scrollRef={scrollRef}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onReachEnd={onReachEnd}
              onReady={setHandle}
            />
          : <RowsSimple
              items={rows as Array<T | null>}
              renderRow={renderRow as (item: T | null, index: number) => React.ReactNode}
              getItemKey={(item, index) => (index < items.length ? getItemKey(item as T, index) : `__skeleton__${index}`)}
              scrollClassName={scrollClassName}
              scrollRef={scrollRef}
              onReady={setHandle}
            />}
    </div>
  );
});

export default LazyList;
